import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  OutlinedInput,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus } from '@styled-icons/bootstrap';
import { Close } from '@styled-icons/evaicons-solid/Close';
import DeletableChip from 'Components/Forms/DeletableChip';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import sortBy from 'lodash/sortBy';
import React, {
  ChangeEvent,
  FC,
  FocusEvent,
  KeyboardEvent,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { stopPropagation } from 'utils/event.utils';

export interface IFilterAutocompleteSelectItem {
  id: string;
  title: string;
  endAdornment?: ReactNode;
  /**
   * Will move the items to the top of the list
   */
  displayFirst?: boolean;
}

interface IFilterAutocompleteSelectionProps {
  label?: string;
  placeholder?: string;
  /**
   * The default searchTerm showed in the input
   */
  defaultSearchValue?: string;
  /**
   * The items need to have an id and a title
   * Alphabetical sorting is applied on the list on the asc title
   */
  items: IFilterAutocompleteSelectItem[];
  /**
   * Determines whether the multiselect should automatically close after a selection is made.
   * Only affects multi select
   */
  size?: 'small' | 'medium';
  closeAfterSelection?: boolean;
  multiple?: boolean;
  defaultOpen?: boolean;
  selected?: string[] | string;
  enableAddNewItem?: boolean;
  itemName?: string;
  hasError?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  'data-testId'?: string;
  onSelectionChange: (selectedItems?: string[] | string) => void;
  onAddNewItemClick?: (value: string) => void;
  onSelectionClose?: (selectedItems?: string[] | string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const StyledSelect = styled(Select)<{ size?: 'small' | 'medium' }>(({ size }) => ({
  '&.MuiInputBase-root': {
    height: size === 'small' ? 32 : 40,
  },
  '& .MuiSelect-select': {
    backgroundColor: 'transparent',
    height: size === 'small' ? 32 : 40,
  },
  '& .MuiChip-sizeSmall': {
    margin: 4,
  },
  '& .MuiChip-sizeMedium': {
    margin: 8,
  },
}));

const StyledSelectInputContainer = styled(Box)(() => ({
  maxWidth: 'calc(100% - 31px)',
  overflow: 'hidden',
  minHeight: '1.1876em',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
}));

const Searchbar = styled(TextField)(({ theme }) => ({
  width: '100%',
  '&.MuiTextField-root': {
    padding: '0 8px 8px 8px',
    background: theme.custom.themeColors.white,
    '& .MuiInputBase-formControl': {
      paddingRight: 0,
    },
    '& .MuiInputBase-input': {
      padding: '10px 16px 10px',
    },
    '& .MuiInput-underline:before, & .MuiInput-underline:hover:before': {
      borderBottom: `1px solid ${theme.custom.themeColors.grayScale[40]}`,
    },
    '& .MuiInput-underline:after': {
      borderBottom: 0,
    },
  },
}));

const StyledLabel = styled(InputLabel)(({ theme }) => ({
  zIndex: 9,
  top: 3,
  fontSize: 12,
  lineHeight: '1.2em !important',
}));

const Placeholder = styled(InputLabel)<{ customSize?: 'small' | 'medium' }>(
  ({ theme, customSize }) => ({
    zIndex: 1,
    fontSize: 12,
    fontWeight: 500,
    color: `${theme.palette.grey[500]} !important`,

    '&.MuiInputLabel-outlined': {
      transform:
        customSize === 'small' ? 'translate(14px, 8px) scale(1)' : 'translate(14px, 12px) scale(1)',
    },
  })
);

const StyledChip = styled(DeletableChip)<{ size?: 'small' | 'medium' }>(({ size }) => ({
  height: size === 'small' ? 20 : 24,
  fontSize: 12,
  marginBottom: 2,
}));

const AddIcon = styled(Plus)(({ theme }) => ({
  color: theme.palette.secondary.main,
  height: 20,
}));

const StyledMenuItem = styled(MenuItem)<{ borderBottom: Tbooleanish }>(
  ({ theme, borderBottom }) => ({
    borderBottom:
      borderBottom === 'true' ? `1px solid ${theme.custom.themeColors.grayScale[40]}` : 'none',
  })
);

const StyledFormHelperText = styled(FormHelperText)(() => ({
  '&.MuiFormHelperText-root': {
    marginTop: -2,
  },
}));

const FilterAutocompleteSelection: FC<IFilterAutocompleteSelectionProps> = ({
  label,
  placeholder,
  defaultSearchValue = '',
  items,
  multiple,
  hasError,
  selected,
  closeAfterSelection,
  enableAddNewItem,
  size = 'small',
  defaultOpen = false,
  itemName = 'item',
  errorMessage = 'This field is required',
  disabled,
  onSelectionChange,
  onAddNewItemClick,
  onSelectionClose,
  onBlur,
}) => {
  const [searchValue, setSearchValue] = useState<string>(defaultSearchValue);
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  const _selected: string | string[] | undefined = useMemo(() => {
    if (!selected) {
      return multiple ? [] : '';
    }
    // Make sure all selected items exist in the options, otherwise material-ui will throw an error
    const selectedIds = Array.isArray(selected) ? selected : [selected];
    if (selectedIds.some(id => !items.some(item => item.id === id))) {
      const filteredSelection = selectedIds.filter(id => items.some(item => item.id === id));
      return multiple ? filteredSelection : filteredSelection[0];
    }
    return selected;
  }, [multiple, selected, items]);

  const sortedListItems = useMemo(
    () =>
      _selected && Array.isArray(_selected)
        ? sortBy(
            items.filter(
              ({ id, title }) =>
                !_selected.includes(id) &&
                ((searchValue &&
                  title.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
                  !searchValue)
            ),
            'title'
          )
        : sortBy(
            items.filter(
              ({ id, title }) =>
                id !== _selected &&
                ((searchValue &&
                  title.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())) ||
                  !searchValue)
            ),
            'title'
          ),
    [items, _selected, searchValue]
  );

  const displayFirstItems = useMemo(
    () => items.filter(({ displayFirst }) => displayFirst),
    [items]
  );

  const sortedSelectedListItems = useMemo(() => {
    if (!items || !_selected) {
      return [];
    }
    return Array.isArray(_selected)
      ? sortBy(
          items.filter(({ id, displayFirst }) => !displayFirst && _selected.includes(id)),
          'title'
        )
      : items.filter(({ id, displayFirst }) => !displayFirst && _selected === id);
  }, [items, _selected]);

  const handleAddNewItemClick = () => onAddNewItemClick?.(searchValue);
  const clearSearchValue = () => setSearchValue('');

  const handleSearchValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setSearchValue(event.target.value);
  };

  const handleMenuClose = (selection: string[] | string | undefined = _selected) => {
    setIsOpen(false);
    onSelectionClose?.(selection);
    setSearchValue('');
  };

  const handleSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  const handleSelectionChange = (event: SelectChangeEvent<unknown>) => {
    const selectedIds = event.target.value as string[];
    if (closeAfterSelection) {
      handleMenuClose(selectedIds);
    }
    onSelectionChange(selectedIds);
  };

  const handleRemove = (itemId: string) => {
    if (multiple && Array.isArray(_selected)) {
      onSelectionChange(_selected.filter(id => id !== itemId));
    } else {
      onSelectionChange(undefined);
    }
  };

  const getSelectedItem = (id: string) => items.find(item => item.id === id);

  const renderSelectedItems = (selected: unknown) => {
    const maxDisplayCount = 3;
    if (multiple && Array.isArray(selected)) {
      return (
        <StyledSelectInputContainer>
          {(selected as string[]).slice(0, maxDisplayCount).map((id, index) => {
            const item = getSelectedItem(id);
            return (
              item && (
                <StyledChip
                  key={id}
                  label={
                    <ThemeTypography variant="BODY_MEDIUM">
                      {maxDisplayCount === index + 1 ? '...' : item.title}
                    </ThemeTypography>
                  }
                  disabled={disabled}
                  onDelete={
                    maxDisplayCount === index + 1
                      ? undefined
                      : () => {
                          handleRemove(item.id);
                        }
                  }
                />
              )
            );
          })}
        </StyledSelectInputContainer>
      );
    }
    const item = getSelectedItem(selected as string);
    return (
      item && (
        <StyledChip
          key={item.id}
          size={size}
          label={<ThemeTypography variant="BODY_MEDIUM">{item.title}</ThemeTypography>}
          onDelete={() => handleRemove(item.id)}
        />
      )
    );
  };

  return (
    <FormControl fullWidth error={hasError}>
      {label && (
        <StyledLabel id="filter-autocomplete-label" shrink variant="outlined" size="small">
          {label}
        </StyledLabel>
      )}
      {!_selected?.length && (
        <Placeholder variant="outlined" customSize={size} shrink={true}>
          {placeholder}
        </Placeholder>
      )}
      <StyledSelect
        size={size}
        labelId="filter-autocomplete-label"
        variant="outlined"
        value={_selected}
        multiple={multiple}
        disabled={disabled}
        MenuProps={{
          autoFocus: false,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        open={isOpen}
        fullWidth
        inputProps={{
          id: 'select-multiple-native',
        }}
        input={<OutlinedInput label={label} notched id="select-multiple-native" />}
        onChange={handleSelectionChange}
        renderValue={renderSelectedItems}
        onOpen={() => setIsOpen(true)}
        onClose={() => handleMenuClose()}
        onBlur={onBlur}
      >
        <Searchbar
          autoFocus
          fullWidth
          value={searchValue}
          placeholder="Type to filter"
          onChange={handleSearchValueChange}
          onKeyDown={handleSearchKeyDown}
          // Prevent searchbar from being selected
          InputProps={{
            value: searchValue,
            endAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={clearSearchValue}>
                  <Close size={16} />
                </IconButton>
              </InputAdornment>
            ),
            onClick: stopPropagation,
          }}
        />

        {sortedSelectedListItems.length &&
          !searchValue &&
          sortedSelectedListItems.map(({ id, title, endAdornment }, index) => (
            <StyledMenuItem
              key={id}
              value={id}
              selected
              borderBottom={Booleanish(index === sortedSelectedListItems.length - 1)}
            >
              {title} {endAdornment ? endAdornment : undefined}
            </StyledMenuItem>
          ))}

        {displayFirstItems.length &&
          displayFirstItems.map(({ id, title, endAdornment }, index) => (
            <StyledMenuItem
              key={id}
              value={id}
              borderBottom={Booleanish(
                index === displayFirstItems.length - 1 &&
                  sortedListItems.length - displayFirstItems.length + 1 > 0
              )}
            >
              {title} {endAdornment ? endAdornment : undefined}
            </StyledMenuItem>
          ))}

        {sortedListItems.length &&
          sortedListItems
            .filter(({ displayFirst }) => !displayFirst)
            .map(({ id, title, endAdornment }) => (
              <MenuItem key={id} value={id}>
                {title} {endAdornment ? endAdornment : undefined}
              </MenuItem>
            ))}

        {!sortedListItems.length && (
          <>
            <Box height={30} mt={2} ml={2}>
              <ThemeTypography variant="BODY_LARGE" color="GRAY_40">
                No results
              </ThemeTypography>
            </Box>
            {enableAddNewItem && (
              <MenuItem value="add-new-item" onClick={handleAddNewItemClick}>
                <AddIcon />
                Add {`"${searchValue}"`} as new {itemName}
              </MenuItem>
            )}
          </>
        )}
      </StyledSelect>
      {hasError && <StyledFormHelperText>{errorMessage}</StyledFormHelperText>}
    </FormControl>
  );
};

export default FilterAutocompleteSelection;
